import { ref } from 'vue';
import {
  useAuthSignInMutation,
  useAuthVerifyOtpMutation,
  useAuthCheckUserExistsLazyQuery,
  useAuthSignUpMutation,
  useAuthForgotPasswordMutation,
  useAuthResendOtpMutation,
  OtpVerificationType as NexusOtpVerificationType,
  SignInProvider,
  AuthCheckUserExistsQueryVariables,
  OtpContext
} from '@/generated/nexus-graphql';
import { useUserActions } from '@/store';
import { onNexusLogin } from '@/apollo/nexusAuth';
import { setRefreshToken } from '@/services/secureStorage';
import { startTokenRefreshTimer } from '@/utils/tokenRefreshTimer';

export interface AuthState {
  phase: 'idle' | 'otp' | 'signupOtp' | 'password' | 'complete' | 'error';
  message?: string;
  newUser?: boolean;
}

export function useAuthFlow() {
  const state = ref<AuthState>({ phase: 'idle' });

  const { loginUser } = useUserActions(['loginUser']);
  const { mutate: signInMutate } = useAuthSignInMutation({ clientId: 'nexus' });
  const { mutate: verifyOtpMutate } = useAuthVerifyOtpMutation({ clientId: 'nexus' });
  const userExistsVars = ref<AuthCheckUserExistsQueryVariables>({
    input: { identifier: '', type: 'EMAIL' }
  });

  const { load: loadUserExists, result: userExistsResult } = useAuthCheckUserExistsLazyQuery(
    userExistsVars,
    {
      clientId: 'nexus',
      fetchPolicy: 'network-only'
    }
  );
  const { mutate: signUpMutate } = useAuthSignUpMutation({ clientId: 'nexus' });
  const { mutate: forgotPasswordMutate } = useAuthForgotPasswordMutation({ clientId: 'nexus' });
  const { mutate: resendOtpMutate } = useAuthResendOtpMutation({ clientId: 'nexus' });

  const currentContact = ref<string>('');
  const currentIsEmail = ref<boolean>(true);

  async function start(contact: string, isEmail: boolean) {
    // Validate input parameters
    if (!contact) {
      console.error('[AuthFlow] Cannot start auth flow with empty contact');
      state.value = { phase: 'error', message: 'Contact information is required' };
      return;
    }

    try {
      currentContact.value = contact;
      currentIsEmail.value = isEmail;

      // Check if user exists
      userExistsVars.value = {
        input: { identifier: contact, type: isEmail ? 'EMAIL' : 'PHONE' }
      };
      await loadUserExists();
      const exists = userExistsResult.value?.authCheckUserExists ?? false;

      if (exists) {
        // Existing user: send OTP
        console.log('[AuthFlow] User exists - sending OTP via sign-in');
        await signInMutate({
          input: {
            provider: isEmail ? SignInProvider.EmailOtp : SignInProvider.PhoneOtp,
            username: contact
          }
        });
        state.value = { phase: 'otp', newUser: false };
      } else {
        // New user path: Attempt sign-up first to avoid duplicate OTPs.
        // If sign-up fails because the user already exists (e.g., present in Auth0 but not in our DB),
        // fall back to sign-in. This ensures we send at most ONE OTP.
        try {
          console.log('[AuthFlow] User not found — signing up');
          const { data } = await signUpMutate({
            input: {
              method: isEmail ? 'EMAIL' : 'PHONE',
              email: isEmail ? contact : undefined,
              phoneNumber: isEmail ? undefined : contact
            }
          });

          // Check if backend redirected to sign-in (indicated by presence of otpSent field)
          // The backend returns a sign-in response when redirecting from sign-up
          if (data?.authSignUp && 'otpSent' in data.authSignUp && data.authSignUp.otpSent) {
            console.log('[AuthFlow] Backend redirected sign-up to sign-in - OTP already sent');
            state.value = { phase: 'otp', newUser: false };
          } else {
            // Normal sign-up success - OTP should be sent
            state.value = { phase: 'signupOtp', newUser: true };
          }
        } catch (signUpError: any) {
          console.log('[AuthFlow] Sign-up failed, checking if it was due to existing user');

          // Check if the error indicates the user already exists and OTP was sent
          const errorMessage = signUpError?.message || '';
          if (
            errorMessage.includes('already exists') ||
            errorMessage.includes('redirecting to sign-in')
          ) {
            console.log('[AuthFlow] User exists - OTP already sent by backend redirect');
            state.value = { phase: 'otp', newUser: false };
          } else {
            // Real error - but don't send another OTP since backend may have already sent one
            console.log(
              '[AuthFlow] Real error occurred during sign-up, setting error state:',
              errorMessage
            );
            state.value = { phase: 'error', message: errorMessage };
          }
        }
      }
    } catch (e) {
      state.value = { phase: 'error', message: (e as Error).message };
    }
  }

  async function loginWithPassword(password: string) {
    try {
      const provider = SignInProvider.EmailPassword; // Only email/password supported
      const { data } = await signInMutate({
        input: {
          provider,
          username: currentContact.value,
          password
        }
      });
      if (!data?.authSignIn?.success) throw new Error(data?.authSignIn?.message ?? 'Login failed');

      await onNexusLogin(data.authSignIn.accessToken);
      if (data.authSignIn.refreshToken) setRefreshToken(data.authSignIn.refreshToken);
      startTokenRefreshTimer();
      await loginUser(data.authSignIn.user);
      state.value = { phase: 'complete' };
    } catch (e) {
      state.value = { phase: 'error', message: (e as Error).message };
    }
  }

  async function forgotPassword() {
    try {
      const { data } = await forgotPasswordMutate({
        input: { email: currentContact.value }
      });
      if (!data?.authForgotPassword?.success) {
        throw new Error(data?.authForgotPassword?.message ?? 'Failed to send reset link');
      }
      return true;
    } catch (e) {
      state.value = { phase: 'error', message: (e as Error).message };
      return false;
    }
  }

  async function verify(code: string) {
    try {
      const result = await verifyOtpMutate({
        input: {
          username: currentContact.value,
          otpCode: code,
          otpType: currentIsEmail.value
            ? NexusOtpVerificationType.Email
            : NexusOtpVerificationType.Phone
        }
      });

      // Check for GraphQL errors first
      if (result?.errors) {
        const errorMessage = result.errors[0]?.message || 'GraphQL error during OTP verification';
        console.error('[AuthFlow] GraphQL error in OTP verification:', errorMessage);
        throw new Error(errorMessage);
      }

      const { data } = result;
      if (!data?.authVerifyOtp?.success) {
        const msg = data?.authVerifyOtp?.message ?? 'Invalid verification code';
        throw new Error(msg);
      }

      await onNexusLogin(data.authVerifyOtp.accessToken);
      if (data.authVerifyOtp.refreshToken) setRefreshToken(data.authVerifyOtp.refreshToken);
      startTokenRefreshTimer();
      await loginUser(data.authVerifyOtp.user);
      state.value = { phase: 'complete', newUser: state.value.newUser };
    } catch (e) {
      state.value = { phase: 'error', message: (e as Error).message };
    }
  }

  async function resend() {
    try {
      const { data } = await resendOtpMutate({
        input: {
          identifier: currentContact.value,
          type: currentIsEmail.value ? 'EMAIL' : 'PHONE',
          context: state.value.newUser ? OtpContext.SignUp : OtpContext.SignIn
        }
      });

      if (!data?.authResendOtp?.success) {
        const message = data?.authResendOtp?.message ?? 'Failed to resend verification code';
        throw new Error(message);
      }

      return {
        success: true,
        message: data.authResendOtp.message,
        otpAlreadySent: data.authResendOtp.otpAlreadySent,
        cooldownMs: data.authResendOtp.resendCooldownMs
      };
    } catch (e) {
      const errorMessage = (e as Error).message;
      // Don't set state to error for resend failures, just throw
      throw new Error(errorMessage);
    }
  }

  return { state, start, verify, loginWithPassword, forgotPassword, resend };
}
